import { createServerClient } from "@supabase/ssr";

interface ApiResponse<T> {
  data: T;
  status: number;
  message?: string;
}

interface ApiError {
  message: string;
  status: number;
}

// 쿠키에서 값을 가져오는 헬퍼 함수
const getCookie = (name: string): string | null => {
  if (typeof document === "undefined") return null;

  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(";").shift() || null;
  return null;
};

// 토큰 관련 함수
export const getAuthToken = (req?: any): string | null => {
  if (typeof window === "undefined") {
    const cookieHeader = req?.headers?.get("cookie");
    if (!cookieHeader) return null;
    const match = cookieHeader.match(/accessToken=([^;]+)/);
    return match ? decodeURIComponent(match[1]) : null;
  }
  return localStorage.getItem("accessToken");
};

export const setAuthToken = (token: string): void => {
  if (typeof window === "undefined") return;
  localStorage.setItem("accessToken", token);
};

// 로그아웃 처리 함수
export const clearAuthData = (): void => {
  if (typeof window === "undefined") return;
  localStorage.removeItem("accessToken");
  localStorage.removeItem("tnmtName");
  localStorage.removeItem("sportsType");
  localStorage.removeItem("tnmtId");
  localStorage.removeItem("tnmtCategory");
  localStorage.removeItem("broadcastYn");
};

// API 요청에 사용할 공통 헤더
const getAuthHeaders = (token?: string, req?: any): HeadersInit => {
  const headers: HeadersInit = {
    "Content-Type": "application/json",
  };
  const authToken = token || getAuthToken(req);
  if (authToken) {
    headers["Authorization"] = `Bearer ${authToken}`;
  }
  return headers;
};

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    // 401 Unauthorized인 경우 로그인 페이지로 리다이렉트
    if (response.status === 401) {
      if (typeof window !== "undefined") {
        localStorage.removeItem("accessToken");
        localStorage.removeItem("tnmtName");
        localStorage.removeItem("sportsType");
        localStorage.removeItem("tnmtId");
        localStorage.removeItem("tnmtCategory");
        localStorage.removeItem("broadcastYn");
        window.location.href = "/login"; // 로그인 페이지로 리다이렉트
      }
    }

    try {
      const errorBody = await response.text();
      console.error(`[API Error] ${response.status} ${response.statusText} - URL: ${response.url}`);
      console.error(`[API Error] Response Body:`, errorBody);
      try {
        const error: ApiError = JSON.parse(errorBody);
        throw new Error(error.message || "API 요청 중 오류가 발생했습니다.");
      } catch (parseError) {
        throw new Error(
          `API 요청 실패: ${response.status} ${response.statusText} - ${errorBody}`,
        );
      }
    } catch (outerError) {
      throw outerError;
    }
  }

  // 응답 본문이 비어있는지 확인
  const contentType = response.headers.get("content-type");
  const hasBody = response.headers.get("content-length") !== "0";

  // 응답이 비어있거나 content-type이 JSON이 아닌 경우
  if (!hasBody || !contentType || !contentType.includes("application/json")) {
    return { success: true, status: response.status } as unknown as T;
  }

  try {
    return (await response.json()) as T;
  } catch (jsonError) {
    console.error("JSON 파싱 오류:", jsonError);
    // 파싱 실패했지만 요청은 성공한 경우
    return { success: true, status: response.status } as unknown as T;
  }
}

const getBaseUrl = () => {
  if (typeof window === "undefined") {
    // 서버사이드: 빌드 타임에 고정된 값 우선 (런타임 Preview env 무시)
    return (import.meta.env.VITE_WEB_SERVICE_URL as string)
      || process.env.VITE_WEB_SERVICE_URL;
  }
  // 클라이언트 사이드: 같은 도메인 (proxy 서버리스가 처리)
  return window.location.origin;
};

export async function get<T>(
  endpoint: string,
  params?: Record<string, string>,
  token?: string,
  req?: any,
): Promise<T> {
  const baseUrl = getBaseUrl();
  const url = new URL(
    endpoint.startsWith("/rest/api") ? endpoint : `/rest/api${endpoint}`,
    baseUrl,
  );
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== null) {
        url.searchParams.append(key, value);
      }
    });
  }

  const response = await fetch(url.toString(), {
    method: "GET",
    headers: getAuthHeaders(token, req),
  });

  return handleResponse<T>(response);
}

export async function post<T>(
  endpoint: string,
  data: unknown,
  token?: string,
  req?: any,
): Promise<T> {
  const baseUrl = getBaseUrl();
  const url = new URL(
    endpoint.startsWith("/rest/api") ? endpoint : `/rest/api${endpoint}`,
    baseUrl,
  );
  const headers = getAuthHeaders(token, req);
  const body = data === undefined ? "" : JSON.stringify(data);
  const response = await fetch(url.toString(), {
    method: "POST",
    headers,
    body,
  });

  return handleResponse<T>(response);
}

// accessToken을 쿠키에서 추출하는 헬퍼 함수 (서버용)
export const getAccessTokenFromCookie = (
  cookieHeader: string | undefined,
): string | null => {
  if (!cookieHeader) return null;
  const match = cookieHeader.match(/accessToken=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : null;
};

// accessToken을 쿠키에 저장하는 헬퍼 함수 (클라이언트용)
export const setAccessTokenToCookie = (
  accessToken: string,
  maxAgeSec: number = 60 * 60 * 24 * 7,
) => {
  if (typeof document === "undefined") return;
  document.cookie = `accessToken=${encodeURIComponent(accessToken)}; path=/; max-age=${maxAgeSec}`;
};